import api from './api'
import {
  Documento,
  UploadResponse,
  AnaliseIaResponse,
  DocumentoConteudoResponse,
  DocumentoVersao,
} from '@/types/documento'
import { PageResponse } from '@/types/comum'
import { MENSAGEM_DOCUMENTO_EXPURGADO, ehDocumentoExpurgado } from '@/utils/documentoExpurgado'

const BASE_URL = '/api/v1/documentos'

/**
 * Limite de upload prometido nas telas ("Tamanho máximo de 25 MB por arquivo"). O nginx do
 * frontend aceita 30 MB no /api (arquivo + envelope multipart) e o serviço de documentos 50 MB;
 * o menor limite, o que o usuário vê, fica aqui — e é conferido ANTES de subir o arquivo.
 */
export const TAMANHO_MAXIMO_UPLOAD_MB = 25
export const TAMANHO_MAXIMO_UPLOAD_BYTES = TAMANHO_MAXIMO_UPLOAD_MB * 1024 * 1024

export const mensagemArquivoGrande = (nome: string): string =>
  `${nome} passa do limite de ${TAMANHO_MAXIMO_UPLOAD_MB} MB por arquivo. Reduza o arquivo (ou divida em partes) e envie de novo.`

export const documentoService = {
  listar: async (page: number = 0, size: number = 10): Promise<PageResponse<Documento>> => {
    const response = await api.get<PageResponse<Documento>>(
      `${BASE_URL}?page=${page}&size=${size}`
    )
    return response
  },

  // O interceptor de `api` já devolve o CORPO da resposta, então não há envelope para
  // desembrulhar: um `.data` aqui leria um campo que o DocumentoResponse não tem e
  // devolveria `undefined`. Era exatamente o que este método fazia enquanto ninguém o
  // chamava.
  buscar: async (id: string): Promise<Documento> => {
    return await api.get<Documento>(`${BASE_URL}/${id}`)
  },

  /**
   * @param exigenciaChave qual item do checklist este arquivo satisfaz. Só o upload feito
   *   PELO checklist informa; anexo de pendência e material avulso não respondem a
   *   exigência nenhuma. Sem ela o backend volta a avaliar a presença pelo tipo — o que,
   *   num produto com dois itens do mesmo tipo, não distingue qual foi entregue.
   */
  upload: async (
    arquivo: File,
    tipo: string,
    entidadeReferencia: string,
    tipoEntidade: string,
    exigenciaChave?: string | null
  ): Promise<UploadResponse> => {
    const formData = new FormData()
    formData.append('arquivo', arquivo)
    formData.append('tipo', tipo)
    formData.append('entidadeReferencia', entidadeReferencia)
    formData.append('tipoEntidade', tipoEntidade)
    if (exigenciaChave) formData.append('exigenciaChave', exigenciaChave)

    // Antes de gastar upload: acima do limite o proxy responde 413 com uma mensagem genérica
    // ("Request failed with status code 413", 09/09) que não diz ao consultor o que fazer.
    if (arquivo.size > TAMANHO_MAXIMO_UPLOAD_BYTES) {
      throw new Error(mensagemArquivoGrande(arquivo.name))
    }

    try {
      return await api.post<UploadResponse>(`${BASE_URL}/upload`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      })
    } catch (e) {
      // 413 vem de um proxy no caminho (nginx, NPM) sem corpo nosso — traduz para a mesma
      // mensagem do limite em vez do texto do axios.
      if ((e as { status?: number })?.status === 413) {
        throw new Error(mensagemArquivoGrande(arquivo.name))
      }
      throw e
    }
  },

  // Lista documentos filtrando por entidade (ex.: tipoEntidade=OPORTUNIDADE
  // e entidadeReferencia=<oportunidadeId>). Sem entidadeReferencia, traz todos
  // os documentos do tipo informado — útil para agregação no frontend.
  listarFiltrado: async (
    tipoEntidade?: string,
    entidadeReferencia?: string,
    page: number = 0,
    size: number = 500
  ): Promise<PageResponse<Documento>> => {
    const params = new URLSearchParams()
    params.append('page', String(page))
    params.append('size', String(size))
    if (tipoEntidade) params.append('tipoEntidade', tipoEntidade)
    if (entidadeReferencia) params.append('entidadeReferencia', entidadeReferencia)
    const response = await api.get<PageResponse<Documento>>(`${BASE_URL}?${params.toString()}`)
    return response
  },

  // Baixa os BYTES do arquivo pelo backend (stream). É o único caminho de download do
  // frontend: o `GET /{id}/download` do backend devolve um presigned URL com o host
  // interno `minio:9000` (inalcançável pelo browser) — por isso não há método para ele.
  baixarArquivo: async (id: string): Promise<Blob> => {
    try {
      const response = await api.get<Blob>(`${BASE_URL}/${id}/arquivo`, {
        responseType: 'blob',
      })
      return response as unknown as Blob
    } catch (e) {
      if (ehDocumentoExpurgado(e)) throw new Error(MENSAGEM_DOCUMENTO_EXPURGADO)
      throw e
    }
  },

  // Reexecuta extração + análise (documentos em ERRO). Retorna o documento atualizado.
  reprocessar: async (id: string): Promise<Documento> => {
    const response = await api.post<Documento>(`${BASE_URL}/${id}/reprocessar`)
    return response as unknown as Documento
  },

  // Texto extraído pelo Tika (404 enquanto o documento não foi processado).
  obterConteudo: async (id: string): Promise<DocumentoConteudoResponse> => {
    return await api.get<DocumentoConteudoResponse>(`${BASE_URL}/${id}/conteudo`)
  },

  // Análise de IA persistida do documento (404 se ainda não houver análise).
  obterAnalise: async (id: string): Promise<AnaliseIaResponse> => {
    return await api.get<AnaliseIaResponse>(`${BASE_URL}/${id}/analise`)
  },

  deletar: async (id: string): Promise<void> => {
    await api.delete(`${BASE_URL}/${id}`)
  },

  /**
   * Remoção lógica (VC-16). Para o CONSULTOR o backend só aceita documento de reserva
   * (OPORTUNIDADE) enviado por ele, com a reserva em RASCUNHO ou COM_PENDENCIA — fora disso
   * 409 com `errorCode` (ver `mensagemErroDocumento`). Mesmo endpoint do `deletar`.
   */
  remover: async (id: string): Promise<void> => {
    await api.delete(`${BASE_URL}/${id}`)
  },

  /**
   * Troca o arquivo de um documento (VC-16): o novo herda tipo, reserva e exigência do
   * antigo, que sai por remoção lógica. 409 `ARQUIVO_IDENTICO_AO_ATUAL`, 503
   * `VERIFICACAO_SEGURANCA_INDISPONIVEL` (antivírus fora — o antigo fica intacto).
   */
  substituir: async (id: string, arquivo: File, motivo?: string): Promise<Documento> => {
    if (arquivo.size > TAMANHO_MAXIMO_UPLOAD_BYTES) {
      throw new Error(mensagemArquivoGrande(arquivo.name))
    }
    const formData = new FormData()
    formData.append('arquivo', arquivo)
    // Motivo da troca (Onda 4, L11): obrigatório no anexo de resposta de pendência (Q38).
    if (motivo && motivo.trim()) formData.append('motivo', motivo.trim())
    try {
      return await api.post<Documento>(`${BASE_URL}/${id}/substituir`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
    } catch (e) {
      if ((e as { status?: number })?.status === 413) {
        throw new Error(mensagemArquivoGrande(arquivo.name))
      }
      throw e
    }
  },

  /**
   * Trilha de versões do documento (Onda 4, L11/Q37): da 1ª à vigente, com data, autor e
   * motivo. Qualquer id da cadeia serve. Consultor só da própria carteira (404 fora dela) e
   * nunca de material de catálogo; o arquivo de versão anterior é só do back-office.
   */
  versoes: async (id: string): Promise<DocumentoVersao[]> => {
    return await api.get<DocumentoVersao[]>(`${BASE_URL}/${id}/versoes`)
  },
}

// Removidos daqui: `analisar`, `listarPorOportunidade`, `buscarPendentes` e
// `listarPorReserva`. Os quatro apontavam para rotas que o DocumentoController não
// expõe (`/analisar`, `/oportunidade/{id}`, `/status/pendentes`, `/reserva/{id}`) e
// teriam dado 404 — nenhum era chamado, então ninguém descobriu. Para filtrar por
// entidade, use `listarFiltrado(tipoEntidade, entidadeReferencia)`, que é a rota real.
